/**
 * StrategicNarrativeView.tsx - Strategic Narrative Mode
 *
 * Answers the user's question: "Why is this important?"
 * Provides a business-language explanation of the causal reasoning
 * without technical jargon (no node IDs, no confidence percentages).
 *
 * Per wobbly-nibbling-chipmunk.md Phase 4
 * Per REASONING_ORACLE_PROTOTYPE_SELECTION_v3.md Section 12.3
 */

import { useMemo } from 'react'
import DOMPurify from 'isomorphic-dompurify'
import type {
  OracleResponse,
  BSCPerspective,
  SynthesizedExplanation,
} from '@models/oracle'
import './StrategicNarrativeView.css'

interface StrategicNarrativeViewProps {
  response: OracleResponse
  isStrategicMode: boolean
  onToggleMode: () => void
}

// BSC perspective descriptions for narrative
const BSC_NARRATIVES: Record<string, { name: string; description: string }> = {
  Learning_Growth: {
    name: 'Learning & Growth',
    description: 'builds organizational capabilities and human capital',
  },
  Internal_Process: {
    name: 'Internal Process',
    description: 'improves operational efficiency and service delivery',
  },
  Customer: {
    name: 'Customer',
    description: 'enhances customer satisfaction and loyalty',
  },
  Financial: {
    name: 'Financial',
    description: 'drives revenue growth and profitability',
  },
}

// Format concept for display (remove _id suffix, replace underscores)
function formatConcept(concept: string): string {
  return concept
    .replace(/_id$/, '')
    .replace(/_/g, ' ')
    .toLowerCase()
}

// Format perspective for display
function formatPerspective(perspective: BSCPerspective | string): string {
  return BSC_NARRATIVES[perspective]?.name || perspective.replace(/_/g, ' ')
}

// Get perspective narrative description
function getPerspectiveDescription(perspective: BSCPerspective | string): string {
  return BSC_NARRATIVES[perspective]?.description || 'impacts business outcomes'
}

// Generate the strategic narrative from Oracle response
function generateStrategicNarrative(response: OracleResponse): string[] {
  const paragraphs: string[] = []
  const anchors = response.classification.anchor_concepts
  const perspectives = response.classification.bsc_perspectives
  const bscFlow = response.explanation.bsc_flow

  // Opening paragraph - what the user asked about
  if (anchors.length >= 2) {
    const concept1 = formatConcept(anchors[0].concept)
    const concept2 = formatConcept(anchors[1]?.concept || anchors[0].concept)
    const persp1 = formatPerspective(perspectives[0])
    const persp2 = formatPerspective(perspectives[perspectives.length - 1])

    paragraphs.push(
      `Your question explores the connection between ${concept1} and ${concept2}—two ` +
      `concepts that span the Balanced Scorecard from ${persp1} to ${persp2}. ` +
      `Understanding this relationship is key to making informed strategic decisions.`
    )
  } else if (anchors.length === 1) {
    const concept = formatConcept(anchors[0].concept)
    paragraphs.push(
      `Your question focuses on ${concept} and its strategic implications across ` +
      `your business. Causeway has traced how this concept connects to broader outcomes.`
    )
  }

  // BSC flow explanation
  if (bscFlow && bscFlow.length > 1) {
    let flowNarrative = 'Causeway traced this relationship through the BSC causal model:\n\n'

    bscFlow.forEach((perspective, idx) => {
      const perspName = formatPerspective(perspective)
      const perspDesc = getPerspectiveDescription(perspective)

      if (idx === 0) {
        flowNarrative += `Starting at **${perspName}**, which ${perspDesc}`
      } else if (idx === bscFlow.length - 1) {
        flowNarrative += `, ultimately reaching **${perspName}**, which ${perspDesc}.`
      } else {
        flowNarrative += `, flowing through **${perspName}**, which ${perspDesc}`
      }
    })

    paragraphs.push(flowNarrative)
  }

  // Why this matters
  const chainCount = response.inference_chains.length
  const totalNodes = new Set(
    response.inference_chains.flatMap((c) => c.matched_nodes)
  ).size

  paragraphs.push(
    `This analysis validated ${totalNodes} business concepts across ${chainCount} ` +
    `causal pathway${chainCount !== 1 ? 's' : ''}, providing evidence-based support ` +
    `for your strategic recommendations. The pillars and tactics in your Strategy ` +
    `Workspace reflect these validated connections.`
  )

  // Confidence interpretation (in business terms, not percentages)
  const confidenceLevel = response.confidence.level
  if (confidenceLevel === 'HIGH') {
    paragraphs.push(
      `The evidence strongly supports these conclusions. Your organization's business ` +
      `model shows clear causal relationships that align with established strategic frameworks.`
    )
  } else if (confidenceLevel === 'MEDIUM') {
    paragraphs.push(
      `The evidence provides good support for these conclusions, though some connections ` +
      `may benefit from further validation as you implement your strategy.`
    )
  } else if (confidenceLevel === 'LOW') {
    paragraphs.push(
      `While these connections appear plausible, the evidence is limited. Consider this ` +
      `analysis as a starting point and gather additional data to strengthen your strategy.`
    )
  } else {
    paragraphs.push(
      `The available evidence is insufficient for strong conclusions. Review the identified ` +
      `gaps and consider expanding your business data before finalizing strategic decisions.`
    )
  }

  // Gaps (if any, in plain language)
  const gaps = response.explanation.gaps_identified
  if (gaps.length > 0) {
    const gapList = gaps.slice(0, 3).map(formatConcept).join(', ')
    const moreGaps = gaps.length > 3 ? ` and ${gaps.length - 3} other areas` : ''

    paragraphs.push(
      `**Note:** Some concepts (${gapList}${moreGaps}) were not found in your business ` +
      `model. Adding these could strengthen future analyses.`
    )
  }

  return paragraphs
}

// Generate BSC flow steps for visual display
function generateBSCFlowSteps(
  bscFlow: string[],
  _explanation: SynthesizedExplanation
): Array<{ perspective: string; insight: string }> {
  return bscFlow.map((perspective) => {
    const perspName = formatPerspective(perspective)
    const perspDesc = getPerspectiveDescription(perspective)

    return {
      perspective: perspName,
      insight: perspDesc.charAt(0).toUpperCase() + perspDesc.slice(1),
    }
  })
}

function StrategicNarrativeView({
  response,
  isStrategicMode,
  onToggleMode,
}: StrategicNarrativeViewProps) {
  // Generate narrative paragraphs
  const narrativeParagraphs = useMemo(
    () => generateStrategicNarrative(response),
    [response]
  )

  // Generate BSC flow steps
  const bscFlowSteps = useMemo(
    () => generateBSCFlowSteps(response.explanation.bsc_flow, response.explanation),
    [response.explanation]
  )

  return (
    <div className="strategic-narrative-view">
      {/* View Toggle */}
      <div className="view-toggle">
        <button
          className={`toggle-btn ${!isStrategicMode ? 'active' : ''}`}
          onClick={() => isStrategicMode && onToggleMode()}
        >
          <span className="toggle-icon">📊</span>
          Technical View
        </button>
        <button
          className={`toggle-btn ${isStrategicMode ? 'active' : ''}`}
          onClick={() => !isStrategicMode && onToggleMode()}
        >
          <span className="toggle-icon">💼</span>
          Strategic View
        </button>
      </div>

      {/* Strategic Narrative Content */}
      {isStrategicMode && (
        <div className="narrative-content">
          {/* Header */}
          <div className="narrative-header">
            <span className="header-icon">💼</span>
            <h3>Strategic Narrative</h3>
          </div>

          {/* Narrative Paragraphs */}
          <div className="narrative-body">
            {narrativeParagraphs.map((paragraph, idx) => {
              const rawHtml = paragraph
                .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                .replace(/\n/g, '<br />')
              const sanitized = DOMPurify.sanitize(rawHtml, {
                ALLOWED_TAGS: ['strong', 'br'],
                ALLOWED_ATTR: [],
              })
              return (
                <p
                  key={idx}
                  className="narrative-paragraph"
                  dangerouslySetInnerHTML={{ __html: sanitized }}
                />
              )
            })}
          </div>

          {/* BSC Flow Visualization */}
          {bscFlowSteps.length > 0 && (
            <div className="bsc-flow-narrative">
              <h4>The Causal Chain</h4>
              <div className="flow-steps">
                {bscFlowSteps.map((step, idx) => (
                  <div key={idx} className="flow-step">
                    <div className="step-perspective">{step.perspective}</div>
                    <div className="step-insight">{step.insight}</div>
                    {idx < bscFlowSteps.length - 1 && (
                      <div className="step-arrow">
                        <span>↓</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Technical View Placeholder */}
      {!isStrategicMode && (
        <div className="technical-view-notice">
          <p>
            Technical details are displayed in the panels above. Switch to{' '}
            <strong>Strategic View</strong> for a business-language summary.
          </p>
        </div>
      )}
    </div>
  )
}

export default StrategicNarrativeView
